import { combineEpics, ofType, StateObservable } from 'redux-observable';
import { Observable, of } from 'rxjs';
import {
  catchError,
  filter,
  groupBy,
  map,
  mergeMap,
  startWith,
  switchMap,
  throttleTime,
} from 'rxjs/operators';
import { requestActions } from './apiHelpers';
import { ID, ReduxAction } from '../types';
import { apiObservable } from '@support/api';
import { AppState } from '@reducers/rootReducer';
import Collection from '@models/Collection';

/* Epic actions */
const sourcesActions = requestActions('SOURCE', ['fetchAll', 'create', 'delete', 'update']);

/* Component actions */
export const fetchSources = sourcesActions['fetchAll'];
export const createSource = sourcesActions['create'];
export const deleteSource = sourcesActions['delete'];
const updateSource = sourcesActions['update'];
export const updateSourceLabel = ({
  contactID,
  sourceID,
  label,
}: {
  contactID: ID;
  sourceID: ID;
  label: string | null;
}) => updateSource({ contactID, sourceID, label });

/* Epics */
const fetchSourcesEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('SELECT_CONTACT', 'FETCH_ALL_SOURCES'),
    // Ignore any actions that set the selected contact to null
    filter(({ payload }) => !!payload?.contact),
    groupBy(({ payload }) => payload.contact.id),
    mergeMap((fetchesByID) =>
      fetchesByID.pipe(
        // For a given contact, only fetch the cadence at most once every 2 minutes
        throttleTime(2 * 60 * 1000),
        switchMap(({ payload }) =>
          apiObservable('GET', `/contacts/${payload.contact.id}/sources/`, undefined, {
            apiVersion: 'v2',
          }).pipe(
            map(({ response }) =>
              sourcesActions['fetchAllSucceeded']({
                sources: response,
                contactID: payload.contact.id,
              }),
            ),
            startWith(
              sourcesActions['fetchAllRequested']({
                contactID: payload.contact.id,
              }),
            ),
            catchError((err) =>
              of(
                sourcesActions['fetchAllFailed']({
                  ...err,
                  contactID: payload.contact.id,
                }),
              ),
            ),
          ),
        ),
      ),
    ),
  );

const createSourceEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('CREATE_SOURCE'),
    mergeMap(({ payload }) => {
      const request: any = { value: payload.value };

      if (payload.clearExisting) {
        request.query = { clear: true };
      }

      return apiObservable('POST', `/network/contacts/${payload.contactID}/sources`, request).pipe(
        map((response) => sourcesActions['createSucceeded']({ ...payload, ...response })),
        startWith(sourcesActions['createRequested'](payload)),
        catchError((error) => of(sourcesActions['createFailed']({ error, ...payload }))),
      );
    }),
  );

// Can we pass `null` to unset label? should be able to...
const updateSourceEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('UPDATE_SOURCE'),
    mergeMap(({ payload }) =>
      apiObservable(
        'PATCH',
        `/contacts/${payload.contactID}/sources/${payload.sourceID}`,
        {
          label: payload.label,
        },
        { apiVersion: 'v2' },
      ).pipe(
        map((response) => sourcesActions['updateSucceeded']({ ...payload, ...response })),
        startWith(sourcesActions['updateRequested'](payload)),
        catchError((error) => of(sourcesActions['updateFailed']({ error, ...payload }))),
      ),
    ),
  );

const deleteSourceEpic = (action$: Observable<ReduxAction>, state$: StateObservable<AppState>) => {
  return action$.pipe(
    ofType('DELETE_SOURCE'),
    mergeMap(({ payload }) => {
      // check the current value of contact.information so we can reuse it if the delete fails
      const initialSourcesCollection = state$.value.data.sources.collection.items.get(
        payload.contactID,
        new Collection(),
      );

      return apiObservable(
        'DELETE',
        `/network/contacts/${payload.contactID}/sources`,
        payload.source,
      ).pipe(
        map(() => sourcesActions['deleteSucceeded'](payload)),
        startWith(sourcesActions['deleteRequested'](payload)),
        catchError((error) =>
          of(
            sourcesActions['deleteFailed']({
              error,
              contactID: payload.contactID,
              initialSourcesCollection,
            }),
          ),
        ),
      );
    }),
  );
};

export const sourcesEpic = combineEpics(
  fetchSourcesEpic,
  createSourceEpic,
  updateSourceEpic,
  deleteSourceEpic,
);
